/**
 * Reusable cover-image picker — auto-crops to 16:9 via the existing
 * /api/upload/game-image endpoint and shows a live preview. Falls back to
 * the supplied default URL (e.g. a default sport image).
 *
 * Usage:
 *   <CoverImagePicker value={form.image_url} onChange={(url) => setForm({...form, image_url: url})} />
 */
import { useState } from "react";
import { Camera, Loader2, ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/App";
import { getAuthToken } from "@/lib/firebase";

export const CoverImagePicker = ({
  value,
  onChange,
  label = "Cover image",
  helper = "Auto-cropped to 16:9. Or paste an image URL below.",
  testidPrefix = "cover",
}) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = await getAuthToken();
      const body = new FormData();
      body.append("file", file);
      const res = await fetch(`${API}/upload/game-image`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body,
      });
      if (!res.ok) {
        const detail = await res.json().then(d => d.detail).catch(() => null);
        throw new Error(detail || "Upload failed");
      }
      const data = await res.json();
      onChange(`${API}/files/${data.storage_path}`);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid={`${testidPrefix}-picker`}>
      {label && <Label className="block mb-1.5">{label}</Label>}
      <div className="relative rounded-xl overflow-hidden border border-border bg-muted/30" style={{ aspectRatio: "16/9" }}>
        {value ? (
          <img src={value} alt="Cover preview" className="w-full h-full object-cover" data-testid={`${testidPrefix}-preview`} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
            <ImageIcon className="w-8 h-8 mb-1" />
            <span className="text-xs">No cover image yet</span>
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}
        <label
          className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 hover:bg-black/90 text-white text-xs font-medium cursor-pointer transition-colors"
          data-testid={`${testidPrefix}-upload-btn`}
        >
          <Camera className="w-3.5 h-3.5" />
          {uploading ? "Uploading..." : (value ? "Replace" : "Upload")}
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
        </label>
      </div>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="…or paste an image URL"
        className="mt-2 text-xs"
        data-testid={`${testidPrefix}-url-input`}
      />
      {helper && <p className="text-xs text-muted-foreground mt-1">{helper}</p>}
    </div>
  );
};

export default CoverImagePicker;
